// src/services/creditService.js

import { fetchWithTrace } from './fetchWithTrace';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5000';


function qUser(userId) {
  const id = userId || localStorage.getItem('userId');
  return id ? `?userId=${encodeURIComponent(id)}` : '';
}

/** Balance & pricebook (read-only) */
export async function getBalance(userId) {
  const { data } = await fetchWithTrace(`${API_BASE}/api/credits/balance${qUser(userId)}`);
  return data;
}

export async function getPricebook() {
  const { data } = await fetchWithTrace(`${API_BASE}/api/credits/pricebook`, {
    credentials: 'include',
  });
  return data;
}

/** Stripe: create Checkout session (hosted page for card + billing) */
export async function createCheckout(packCode, userId, customAmount) {
  const { data } = await fetchWithTrace(`${API_BASE}/api/credits/checkout${qUser(userId)}`, {
    method: 'POST',
    credentials: 'include',
    body: { packCode, customAmount },
  });
  return data;
}

/** Stripe: Customer Portal (manage card, invoices) */
export async function openBillingPortal(userId) {
  const { data } = await fetchWithTrace(`${API_BASE}/api/credits/portal${qUser(userId)}`, {
    method: 'POST',
    credentials: 'include',
  });
  return data;
}


/** Stripe: list saved card payment methods for the current user */
export async function listPaymentMethods(userId) {
  // Returns: { methods: [ { id, brand, last4, expMonth, expYear, isDefault }, ... ] }
  const { data } = await fetchWithTrace(
    `${API_BASE}/api/credits/payment-methods${qUser(userId)}`,
    {
      credentials: 'include',
    }
  );
  return data;
}

/** Stripe: set which payment method is default for one-click purchases */
export async function setDefaultPaymentMethod(paymentMethodId, userId) {
  // Backend will also update wallet.defaultPaymentMethodId + cardBrand/last4/exp*
  const { data } = await fetchWithTrace(
    `${API_BASE}/api/credits/set-default-payment-method${qUser(userId)}`,
    {
      method: 'POST',
      credentials: 'include',
      body: { paymentMethodId },
    }
  );
  return data; // { brand, last4, expMonth, expYear }
}



/** Quotes & usage */
export async function quote(code, units = 1, userId) {
  const { data } = await fetchWithTrace(`${API_BASE}/api/credits/quote${qUser(userId)}`, {
    method: 'POST',
    credentials: 'include',
    body: { code, units },
  });
  return data; // { cost, balance, canAfford }
}

export async function reserve(code, units, idempotencyKey, metadata = {}, userId) {
  const { data } = await fetchWithTrace(`${API_BASE}/api/credits/reserve${qUser(userId)}`, {
    method: 'POST',
    credentials: 'include',
    body: { code, units, idempotencyKey, metadata },
  });
  return data; // { ledgerId, cost, balance }
}

export async function finalize(ledgerId, artifactId, userId) {
  const { data } = await fetchWithTrace(`${API_BASE}/api/credits/finalize${qUser(userId)}`, {
    method: 'POST',
    credentials: 'include',
    body: { ledgerId, artifactId },
  });
  try { window.dispatchEvent(new Event('wallet:refresh')); } catch { }
  return data; // { ok: true }
}

export async function revert(ledgerId, reason = 'error', userId) {
  const { data } = await fetchWithTrace(`${API_BASE}/api/credits/revert${qUser(userId)}`, {
    method: 'POST',
    credentials: 'include',
    body: { ledgerId, reason },
  });
  try { window.dispatchEvent(new Event('wallet:refresh')); } catch { }
  return data; // { ok: true, refunded }
}

/** One-shot debit (for fast ops; not used by Workbench yet) */
export async function debit(code, units, idempotencyKey, metadata = {}, userId, allowOverdraft = false) {
  const { data } = await fetchWithTrace(`${API_BASE}/api/credits/debit${qUser(userId)}`, {
    method: 'POST',
    credentials: 'include',
    body: { code, units, idempotencyKey, metadata, allowOverdraft },
  });
  try { window.dispatchEvent(new Event('wallet:refresh')); } catch { }
  return data; // { ledgerId, cost, balance }
}


// --- NEW: card setup / payment method management ---

/**
 * Create a Stripe SetupIntent so we can collect a card with Stripe Elements
 * inside the wallet flyout.
 *
 * Returns: { clientSecret }
 */
export async function createSetupIntent(userId) {
  const { data } = await fetchWithTrace(`${API_BASE}/api/credits/setup-intent${qUser(userId)}`, {
    method: 'POST',
    credentials: 'include',
  });
  return data; // { clientSecret }
}

/**
 * Save a confirmed PaymentMethod (pm_xxx) as the default card for this wallet.
 *
 * Body: { paymentMethodId }
 * Returns: { ok, brand, last4, expMonth, expYear }
 */
export async function savePaymentMethod(paymentMethodId, userId) {
  const { data } = await fetchWithTrace(`${API_BASE}/api/credits/save-payment-method${qUser(userId)}`, {
    method: 'POST',
    credentials: 'include',
    body: { paymentMethodId },
  });
  return data;
}


// --- Insufficient funds signaling (front-end only) ---
// In-memory state only (clears on refresh)
let _insufficientState = null;
const INSUFF_KEY = 'alekhine.wallet.insufficient';

export function readInsufficientFlag() {
  // Cleanup old persistent flags so they don't stick around from previous versions
  try { localStorage.removeItem(INSUFF_KEY); sessionStorage.removeItem(INSUFF_KEY); } catch { }
  return _insufficientState;
}

export function setInsufficientFlag(meta) {
  _insufficientState = meta;
  try { window.dispatchEvent(new Event('wallet:insufficient')); } catch { }
}

export function clearInsufficientFlag() {
  _insufficientState = null;
  // Cleanup old persistent keys just in case
  try { sessionStorage.removeItem(INSUFF_KEY); localStorage.removeItem(INSUFF_KEY); } catch { }
  try { window.dispatchEvent(new Event('wallet:insufficient.cleared')); } catch { }
}

/**
 * Instant purchase using the saved default card (no redirect).
 *
 * Body: { packCode }
 * Returns: { ok, packCode, credits, balance, paymentIntentId }
 */
export async function instantPurchase(packCode, userId, customAmount) {
  const { data } = await fetchWithTrace(`${API_BASE}/api/credits/instant-purchase${qUser(userId)}`, {
    method: 'POST',
    credentials: 'include',
    body: { packCode, customAmount },
  });
  return data;
}